import React, { useState } from 'react';
import db from '../lib/instant';
import TaskCard from '../components/TaskCard';
import CreateTaskModal from '../components/CreateTaskModal';
import CreatePersonalTaskModal from '../components/CreatePersonalTaskModal';

const MyTasks = () => {
  const user = db.useUser();
  const [activeFilter, setActiveFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPersonalTaskModal, setShowPersonalTaskModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');

  // Get all data
  const { data, error } = db.useQuery(user ? {
    tasks: {},
    projects: {},
    project_members: {},
    users: {}
  } : {});

  // Filter user's projects and tasks
  const userProjects = data?.project_members ? 
    data.project_members.filter(pm => pm.user_id === user?.id) : [];
  
  const userProjectIds = userProjects.map(pm => pm.project_id);
  
  const allTasks = data?.tasks ? 
    data.tasks.filter(task => 
      task.assigned_to === user?.id || task.created_by === user?.id
    ) : [];

  const filteredTasks = allTasks.filter(task => {
    switch (activeFilter) {
      case 'completed': return task.status === 'completed';
      case 'pending': return task.status !== 'completed';
      case 'assigned': return task.assigned_to === user?.id;
      case 'created': return task.created_by === user?.id;
      case 'personal': return task.project_id === null;
      case 'project': return task.project_id !== null;
      default: return true;
    }
  });

  const getTaskStats = () => {
    const total = allTasks.length;
    const completed = allTasks.filter(t => t.status === 'completed').length;
    const pending = allTasks.filter(t => t.status !== 'completed').length;
    const overdue = allTasks.filter(t => 
      t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed'
    ).length;

    return { total, pending, completed, overdue };
  };

  const stats = getTaskStats();

  const handleTaskUpdated = () => {
    // Tasks will auto-refresh due to InstantDB reactivity
  };

  const filterButtons = [
    { key: 'all', label: 'All', count: stats.total },
    { key: 'pending', label: 'Not Completed', count: stats.pending },
    { key: 'completed', label: 'Completed', count: stats.completed },
    { key: 'assigned', label: 'Assigned to Me', count: allTasks.filter(t => t.assigned_to === user?.id).length },
    { key: 'created', label: 'Created by Me', count: allTasks.filter(t => t.created_by === user?.id).length }
  ];

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">Error loading tasks. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Tasks</h1>
          {stats.overdue > 0 && (
            <p className="text-red-600 mt-1">{stats.overdue} overdue tasks</p>
          )}
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setShowPersonalTaskModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
            </svg>
            <span>Personal Task</span>
          </button>
          {userProjects.length > 0 && (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>Project Task</span>
            </button>
          )}
        </div>
      </div>

      
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex flex-wrap gap-2">
            {filterButtons.map(button => (
              <button
                key={button.key}
                onClick={() => setActiveFilter(button.key)}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  activeFilter === button.key
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span>{button.label}</span>
                {button.count > 0 && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeFilter === button.key
                      ? 'bg-blue-200 text-blue-800'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {button.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        <div className="p-6">
          {filteredTasks.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <p>
                {activeFilter === 'all' 
                  ? 'No tasks yet. Create your first task to get started!'
                  : `No ${activeFilter.replace('_', ' ')} tasks found.`
                }
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTasks.map((task) => (
                <TaskCard 
                  key={task.id} 
                  task={task} 
                  onTaskUpdated={handleTaskUpdated}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateTaskModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        projectId={selectedProject || (userProjects[0]?.project_id)}
        onTaskCreated={handleTaskUpdated}
      />

      <CreatePersonalTaskModal
        isOpen={showPersonalTaskModal}
        onClose={() => setShowPersonalTaskModal(false)}
        onTaskCreated={handleTaskUpdated}
      />
    </div>
  );
};

export default MyTasks;